import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageMeta from "@/components/PageMeta";
import { FaqJsonLd } from "@/components/JsonLd";
import { resourceGuides } from "@/data/resourceGuides";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" as const },
  }),
};

type FAQItem = { question: string; answer: string };
type FAQCategory = { category: string; items: FAQItem[] };

const ResourcesPage = () => {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const [openItem, setOpenItem] = useState<string | null>(null);

  const categoriesRaw = t("resources.faq", { returnObjects: true });
  const categories = Array.isArray(categoriesRaw) ? categoriesRaw as FAQCategory[] : [];

  const guides = resourceGuides[isDe ? "de" : "en"];

  const toggle = (key: string) => setOpenItem(openItem === key ? null : key);

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title={isDe ? "Wissen & FAQ zu Agentic Ops und KI-Sichtbarkeit | rawResponse Labs" : "Guides & FAQ on agentic ops and AI visibility | rawResponse Labs"}
        description={isDe ? "Praktische Guides für agentische Delivery und KI-Sichtbarkeit. Antworten zu Freigabe, Messung, Betrieb und Übergabe." : "Practical guides to agentic delivery and AI visibility. Answers on approval, measurement, operation and handover."}
      />
      <FaqJsonLd categories={categories} />
      <Navbar />
      <main>


      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="container max-w-3xl">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4"
          >
            {t("resources.label")}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="text-[clamp(2rem,5vw,3.25rem)] font-semibold tracking-tight leading-[1.08] mb-6"
          >
            {t("resources.headline")}
            <br />
            <span className="text-muted-foreground">{t("resources.headlineMuted")}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-[15px] font-mono text-muted-foreground leading-relaxed max-w-lg"
          >
            {t("resources.description")}
          </motion.p>
          <nav aria-label={isDe ? "Guides" : "Guides"} className="mt-8 flex flex-col gap-4 border-t border-border pt-6">
            {guides.map((guide) => <Link key={guide.slug} to={`/resources/${guide.slug}`} className="text-[16px] leading-relaxed underline-offset-4 hover:underline">{guide.title} ↗</Link>)}
          </nav>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-3xl">
          {categories.map((cat, catIdx) => (
            <motion.div
              key={cat.category}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-40px" }}
              custom={catIdx}
              variants={fadeUp}
              className="mb-10"
            >
              <h2 className="text-[13px] font-mono font-medium text-primary uppercase tracking-wider mb-4">
                {cat.category}
              </h2>
              <div className="space-y-1">
                {cat.items.map((item, itemIdx) => {
                  const key = `${catIdx}-${itemIdx}`;
                  const isOpen = openItem === key;
                  return (
                    <div
                      key={key}
                      className="rounded-lg border border-border bg-card overflow-hidden"
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left"
                      >
                        <span className="text-[13px] font-mono font-medium text-foreground/90 pr-4">
                          {item.question}
                        </span>
                        <ChevronDown
                          size={14}
                          className={`text-muted-foreground shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-out ${
                          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                        }`}
                      >
                        <p className="px-5 pb-5 text-[12px] font-mono text-muted-foreground leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Guides teasers */}
      <section className="pb-24 md:pb-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="container max-w-3xl pt-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            custom={0}
            variants={fadeUp}
            className="mb-8"
          >
            <p className="text-[11px] font-mono text-primary uppercase tracking-widest mb-4">
              {t("resourcesGuides.label")}
            </p>
            <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15]">
              {t("resourcesGuides.headline")}
            </h2>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-4">
            {guides.map((item, i) => (
              <motion.div
                key={item.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                custom={i}
                variants={fadeUp}
                className="rounded-xl border border-border bg-card p-6 flex flex-col justify-between"
              >
                <div>
                  <h3 className="text-[14px] font-mono font-medium mb-2">{item.title}</h3>
                  <p className="text-[12px] font-mono text-muted-foreground leading-relaxed mb-4">
                    {item.description}
                  </p>
                </div>
                <Link
                  to={`/resources/${item.slug}`}
                  className="inline-flex items-center gap-1.5 text-[12px] font-mono font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  {isDe ? "Guide lesen" : "Read the guide"}
                  <ArrowRight size={12} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32 relative">
        <div className="section-divider w-full absolute top-0" />
        <div className="container max-w-3xl pt-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="text-center"
          >
            <h2 className="text-[clamp(1.5rem,3.5vw,2.25rem)] font-semibold tracking-tight leading-[1.15] mb-4">
              {t("resourcesCta.headline")}
            </h2>
            <p className="text-[14px] font-mono text-muted-foreground leading-relaxed mb-8 max-w-md mx-auto">
              {isDe ? "Besprechen wir den wiederkehrenden Prozess, den Ihr Team als Nächstes vereinfachen möchte." : "Let's discuss the recurring process your team wants to simplify next."}
            </p>
            <div className="flex items-center justify-center gap-3">
              <a
                href="/contact"
                className="inline-flex items-center gap-1.5 h-9 px-5 rounded-md bg-primary text-primary-foreground text-[13px] font-mono font-medium hover:bg-primary/90 transition-colors"
              >
                {t("resourcesCta.contactButton")}
                <ArrowRight size={13} />
              </a>
              <a
                href="https://webblitz.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 h-9 px-5 rounded-md border border-border text-[13px] font-mono text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
              >
                {t("resourcesCta.tryButton")}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default ResourcesPage;
